import classnames from 'classnames';
import Link from 'next/link';
import React, { CSSProperties } from 'react';

import { IconClose16 } from '../icons/ic-close';

export type BadgeProps = {
    className?: string;
    id?: string;
    leadingIcon?: string | React.JSX.Element;
    name: string | React.JSX.Element;
    textColor?: string;
    bgColor?: string;
    badgeStyle?: BadgeStyle;
    hoverable?: boolean;
    href?: string;
    onBadgeClick?: (badgeId: string, badgeTitle: string) => void;
    wrap?: string;
    style?: CSSProperties;
    isSelected?: boolean;
};

export enum BadgeStyle {
    default = 'text-body-10 rounded-16 uppercase px-6 py-2',
    large = 'text-body-bold-12 rounded-16 lowercase px-6 py-1',
    large_xl = 'text-body-12 uppercase rounded-16 px-12 py-8',
    large_xxl = 'text-overline-16 uppercase rounded-16 px-16 py-1',
    large_default_case = 'text-body-bold-14 rounded-16 px-6 py-1',
    frameless = 'text-body-bold-12 text-gray-light/50 rounded-16 lowercase px-6 py-1',
    filter = 'text-body-bold-12 rounded-16 px-8 py-4 leading-[20px]',
    filter12 = 'text-body-bold-12 rounded-16 px-12 py-4',
    /** Filter-sheet chip: square-ish corners instead of the pill shape of `filter`. */
    filter_sheet = 'text-body-bold-12 rounded-4 px-8 py-4 leading-[20px] tracking-regular'
}

const BADGE_STYLE_DEFAULT_OPACITY: Partial<Record<BadgeStyle, '/20' | '/0'>> = {
    [BadgeStyle.default]: '/20',
    [BadgeStyle.large]: '/20',
    [BadgeStyle.large_default_case]: '/20',
    [BadgeStyle.frameless]: '/0'
};

function applyDefaultOpacity(bgColor: string, badgeStyle: BadgeStyle): string {
    if (bgColor.includes('/')) return bgColor;
    const suffix = BADGE_STYLE_DEFAULT_OPACITY[badgeStyle];
    return suffix ? `${bgColor}${suffix}` : bgColor;
}

function Badge(props: BadgeProps): React.JSX.Element {
    const badgeStyle = props.badgeStyle || BadgeStyle.default;
    const bgColor = applyDefaultOpacity(props.bgColor || 'bg-white', badgeStyle);

    let hoverableStyle = '';
    if (props.hoverable) {
        hoverableStyle = `hover:${
            props.bgColor || 'bg-white'
        } transition-all duration-150 hover:opacity-80 active:opacity-50 cursor-pointer`;
    }

    function onClickInternal(event, badgeId, badgeTitle) {
        if (props.onBadgeClick) {
            props.onBadgeClick(badgeId, badgeTitle);
        }
    }

    const Component = props.onBadgeClick ? 'button' : 'div';

    const badge = (
        <div
            className={classnames('flex items-center max-w-full')}
            title={typeof props.name === 'string' ? props.name : undefined}
            style={props.style}>
            <Component
                type={Component === 'button' ? 'button' : undefined}
                className={classnames(
                    props.className,
                    badgeStyle,
                    bgColor,
                    props.textColor || 'text-gray-light',
                    hoverableStyle,
                    props.wrap || 'whitespace-nowrap',
                    'font-montserrat-semibold truncate',
                    'select-none',
                    // Row layout whenever the badge holds more than its label. This used to be tied
                    // to `isSelected` alone, so an unselected badge with a `leadingIcon` had no flex
                    // context at all and the icon wrapped onto its own line above the text.
                    {
                        'space-x-6 flex flex-row items-center': props.isSelected || !!props.leadingIcon
                    }
                )}
                onClick={(e) => {
                    onClickInternal(e, props.id, props.name);
                }}>
                {props.leadingIcon}
                {props.name && <span className="truncate">{props.name}</span>}
                {props.isSelected && <IconClose16 className="opacity-20" />}
            </Component>
        </div>
    );

    return props.href && props.href.startsWith('/') ? (
        <Link href={props.href}>{badge}</Link>
    ) : props.href ? (
        <a href={props.href}>{badge}</a>
    ) : (
        badge
    );
}

export default Badge;
